import { LinearProgress, styled } from '@mui/material';
import { forwardRef, useEffect, useState } from 'react';

const CustomLinearProgress = styled(LinearProgress)(({ theme }) => {
    return {
        borderRadius: '4px',
        width: '100%',
        height: '8px',
        background: theme.palette.ensightful.neutral[200],
        '& .MuiLinearProgress-bar': {
            borderRadius: '4px',
        },
    };
});

/**
 * A linear progress bar that supports simulated progress updates.
 *
 * @param {number} value - The current value of the progress bar. Expected to be between 0 and 100.
 * @param {Object} [simulateProgress] - Configuration for simulating progress.
 * @param {number} simulateProgress.maxValue - The maximum value for the simulation. Must be greater than 0, typically 100.
 * @param {number} simulateProgress.duration - The duration in seconds for the simulation to complete.
 * @param {React.Ref<HTMLDivElement>} ref - The ref forwarded to the CustomLinearProgress component.
 * @returns {JSX.Element} The rendered progress bar.
 */
const ProgressBar = forwardRef(({ value, simulateProgress, ...props }, ref) => {
    if (simulateProgress?.maxValue === 0) {
        throw new Error(`Shell: Invalid value for "simulateProgress.maxValue" in component ProgressBar.`);
    }

    const [displayValue, setDisplayValue] = useState(0);

    const shouldSimulate = Boolean(simulateProgress);
    const timeoutDuration = shouldSimulate ? (simulateProgress.duration * 1000) / simulateProgress.maxValue : 0;
    const valueToRender = shouldSimulate ? Math.max(value, displayValue) : value;

    useEffect(() => {
        if (!shouldSimulate) return;
        if (displayValue >= simulateProgress.maxValue) return;
        if (value > displayValue) return;
        const randomFactor = Math.random() * (10 - 0.1) + 0.1;
        setTimeout(() => {
            setDisplayValue((current) => {
                const values = [1, 2, 3, 4, 5];
                const randomValue = values[Math.floor(Math.random() * values.length)];
                return current + randomValue;
            });
        }, timeoutDuration * randomFactor);
    }, [displayValue, shouldSimulate, simulateProgress?.maxValue, timeoutDuration, value]);

    return (
        <CustomLinearProgress
            ref={ref}
            variant={'determinate'}
            value={valueToRender}
            {...props}
            aria-label='progress-bar'
        />
    );
});

export default ProgressBar;
